import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usersAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

import ConversationList from '../components/Chat/ConversationList';
import DMChat from '../components/Chat/DMChat';

import { MessageCircle } from 'lucide-react';

import '../components/Chat/Chat.css';

export default function ChatPage() {
  const { userId } = useParams();
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // ============================================================
  // LOAD USERS
  // ============================================================

  useEffect(() => {
    let mounted = true;

    const loadUsers = async () => {
      try {
        setLoading(true);

        const response = await usersAPI.getAll();

        if (!mounted) return;

        const data = response?.data;

        let userList = [];

        if (Array.isArray(data)) {
          userList = data;
        } else if (Array.isArray(data?.users)) {
          userList = data.users;
        } else if (Array.isArray(data?.data)) {
          userList = data.data;
        }

        const currentUserId = user?._id || user?.id;

        // Remove logged-in user from contacts
        if (currentUserId) {
          userList = userList.filter((item) => {
            const id = item?._id || item?.id;

            return String(id) !== String(currentUserId);
          });
        }

        setUsers(userList);
      } catch (error) {
        console.error('Failed to load contacts:', error);

        if (mounted) {
          toast.error('Could not load contacts');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    if (user) {
      loadUsers();
    } else {
      setUsers([]);
      setLoading(false);
    }

    return () => {
      mounted = false;
    };

    // toast is intentionally excluded because useToast()
    // normally returns a stable toast object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ============================================================
  // SELECT USER FROM URL
  // ============================================================

  useEffect(() => {
    // No user ID -> show empty chat screen
    if (!userId) {
      setSelectedUser(null);
      return;
    }

    // Still loading users
    if (loading) {
      return;
    }

    const foundUser = users.find((item) => {
      const id = item?._id || item?.id;

      return String(id) === String(userId);
    });

    if (foundUser) {
      setSelectedUser(foundUser);
    } else {
      /*
       * User may not be returned by /users.
       * Try loading that user directly.
       */
      const loadSelectedUser = async () => {
        try {
          const response = await usersAPI.getById(userId);

          const data = response?.data;

          const fetchedUser =
            data?.user ||
            data?.data ||
            data;

          if (fetchedUser) {
            setSelectedUser(fetchedUser);

            // Add to users list if not already there
            setUsers((prev) => {
              const exists = prev.some((item) => {
                const id = item?._id || item?.id;

                return String(id) === String(userId);
              });

              return exists ? prev : [...prev, fetchedUser];
            });
          } else {
            setSelectedUser(null);
          }
        } catch (error) {
          console.error('Could not load selected user:', error);

          setSelectedUser(null);

          toast.error('Could not open this conversation');
        }
      };

      loadSelectedUser();
    }
  }, [userId, users, loading, toast]);

  // ============================================================
  // SELECT CONTACT
  // ============================================================

  const handleSelectUser = useCallback(
    (contact) => {
      if (!contact) {
        return;
      }

      const id = contact?._id || contact?.id;

      if (!id) {
        toast.error('Invalid contact');
        return;
      }

      // Immediately display selected user
      setSelectedUser(contact);

      // Open conversation
      navigate(`/chat/${id}`);
    },
    [navigate, toast]
  );

  // ============================================================
  // BACK TO CHAT LIST
  // ============================================================

  const handleBackToList = useCallback(() => {
    setSelectedUser(null);
    navigate('/chat');
  }, [navigate]);

  // ============================================================
  // NOT LOGGED IN
  // ============================================================

  if (!user) {
    return null;
  }

  // ============================================================
  // PAGE
  // ============================================================

  return (
    <div className="chat-page">

      {/* ========================================================
          LEFT: CONVERSATION LIST
      ========================================================= */}

      <ConversationList
        users={users}
        loading={loading}
        selectedUserId={userId}
        onSelectUser={handleSelectUser}
        currentUser={user}
      />

      {/* ========================================================
          RIGHT: CHAT
      ========================================================= */}

      <main className="chat-main">

        {selectedUser ? (
          <DMChat
            key={selectedUser._id || selectedUser.id}
            otherUser={selectedUser}
            currentUser={user}
            onBack={handleBackToList}
          />
        ) : (
          <div className="chat-empty-state">

            <div className="chat-empty-icon">
              <MessageCircle
                size={56}
                strokeWidth={1.5}
              />
            </div>

            <h2>Your Messages</h2>

            <p>
              Select a contact to start a private
              conversation
            </p>

          </div>
        )}

      </main>
    </div>
  );
}